import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  LinearProgress,
  Collapse,
  Button,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Terminal as TerminalIcon,
  Code as CodeIcon,
  StickyNote2 as FileIcon,
  BugReport as BugReportIcon,
  Assessment as AssessmentIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew
} from '@mui/icons-material';
import { getSolution, Status, SolutionFile, solveWithAnaV2, getCurrentUser, Extra, getJiraConfig } from 'services/api';
import { useTheme } from '@mui/material/styles';
import { error as error_log, log } from 'utils/logger';
import FileDiffViewer from './FileDiffViewer';

interface AnaSolutionDialogProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  finding_id?: number;
  scanType?: 'SAST' | 'DAST' | 'SCA' | 'SBOM' | 'API' | 'SECRET' | 'INFRA';
}

export const AnaSolutionDialogV2: React.FC<AnaSolutionDialogProps> = ({
  open,
  onClose,
  loading = false,
  finding_id,
  scanType
}) => {
  const theme = useTheme();
  const [status, setStatus] = useState<'pending' | 'completed' | 'failed'>('pending');
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [solutionStatus, setSolutionStatus] = useState<Status | null>(null);
  const [solution, setSolution] = useState<SolutionFile[] | []>([]);
  const [displayedSteps, setDisplayedSteps] = useState<string[]>([]);
  const [isTerminalExpanded, setIsTerminalExpanded] = useState<boolean>(true);
  const [isIssueDetailsExpanded, setIsIssueDetailsExpanded] = useState<boolean>(false);
  const [extraDetails, setExtraDetails] = useState<Extra | null>(null);
  const [jiraTaskUrl, setJiraTaskUrl] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(true);

  // Collapse terminal when files are available
  useEffect(() => {
    if (solution?.length > 0) {
      setIsTerminalExpanded(false);
    }
  }, [solution]);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [copiedOriginal, setCopiedOriginal] = useState<number | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const TOTAL_STEPS = 15;
  const intervalIdRef = useRef<NodeJS.Timeout>();

  // Update polling state when status changes
  useEffect(() => {
    if (status === 'completed' || status === 'failed') {
      setIsPolling(false);
    } else {
      setIsPolling(true);
    }
  }, [status]);

  const checkSolutionStatus = useCallback(async () => {
    if (!finding_id) return;

    try {
      log(`Checking solution status for finding ID: ${finding_id}`);
      const data = await getSolution(finding_id, false);

      setSolution(data.fixes);
      setIsAnalyzing(false);
      setExtraDetails(data.extra || null);

      let currentJiraIssueKey: string | null = null;
      if (data.extra.metadata?.key) {
        currentJiraIssueKey = data.extra.metadata.key;
      }
      // setJiraIssueKey(currentJiraIssueKey);

      // Construct Jira task URL if a key is available
      if (currentJiraIssueKey) {
        try {
          const jiraConfig = await getJiraConfig();
          if (jiraConfig && jiraConfig.base_url) {
            setJiraTaskUrl(`${jiraConfig.base_url}/browse/${currentJiraIssueKey}`);
          } else {
            setJiraTaskUrl(null);
          }
        } catch (jiraError) {
          error_log('Error fetching Jira config or constructing URL:', jiraError);
          setJiraTaskUrl(null);
        }
      } else {
        setJiraTaskUrl(null);
      }

      // Sort steps numerically
      const statusEntries = Object.entries(data.status);
      const sortedSteps = statusEntries
        .filter(([key]) => key !== 'Step Last') // Temporarily exclude "Step Last" for sorting
        .sort(([keyA], [keyB]) => {
          // Extract step numbers for sorting
          const stepNumA = parseInt(keyA.replace('Step ', '')) || 0;
          const stepNumB = parseInt(keyB.replace('Step ', '')) || 0;
          return stepNumA - stepNumB;
        })
        .map(([, value]) => value as string);

      // Add "Step Last" at the end if it exists
      const steps = [...sortedSteps];
      if (data.status["Step Last"]) {
        steps.push(data.status["Step Last"]);
      }
      const lastStep = steps[steps.length - 1] || '';
      const isLastStepFinished = lastStep.toLowerCase().includes('finished') || lastStep.toLowerCase().includes('complete');

      // Extract PR URL from array string format and check for additional content
      let prUrl = data.pr_url;
      if (prUrl && typeof prUrl === 'string') {
        // Remove array brackets and quotes
        prUrl = prUrl.replace(/[[\]']/g, '');

        // Check if URL contains anything else besides a valid GitHub/Bitbucket URL
        const validUrlRegex = /^https?:\/\/(?:www\.)?(?:github\.com|bitbucket\.org)\/[^\s]+\/[^\s]+$/;
        if (prUrl && !validUrlRegex.test(prUrl)) {
          setShowErrorPopup(true);
          prUrl = ''; // Clear invalid URL
        }
      }

      // If no valid PR URL in data.pr_url, look through steps
      if (!prUrl || prUrl === '') {
        const prUrlRegex = /https?:\/\/(?:www\.)?(?:github\.com|bitbucket\.org)\/[^\s]+\/[^\s]+/;
        for (const step of steps) {
          const match = step.match(prUrlRegex);
          if (match) {
            prUrl = match[0];
            // Ensure the step with URL is shown
            if (!steps.includes(step)) {
              steps.push(step);
            }
            break;
          }
        }
      }

      // Update solution status with cleaned PR URL
      setSolutionStatus({
        ...data.status,
        pr_url: prUrl
      });

      // Update displayed steps
      setDisplayedSteps(steps);

      // Check if the process is complete
      const isComplete = isLastStepFinished ||
        (solutionStatus?.pr_url && solutionStatus.pr_url !== '[]') ||
        data.status["Step Last"]?.toLowerCase().includes('finished') ||
        data.status["Step Last"]?.toLowerCase().includes('complete');

      // If process is complete, set status to completed
      if (isComplete) {
        log(`Solution process complete for finding ID: ${finding_id}`);
        setStatus('completed');
        setIsPolling(false);
      }
    } catch (err: any) {
      error_log('Error checking solution status:', err);

      // Check if it's an authentication error
      if (err.response?.status === 401 || err.response?.status === 403) {
        log('Authentication error during solution polling, stopping poll');
        // setError('Authentication expired. Please refresh the page.');
        setStatus('failed');
        setIsPolling(false);
        return;
      }

      // For other errors, only show error message but don't stop polling immediately
      // This prevents temporary network issues from breaking the polling
      const errorMessage = err instanceof Error ? err.message : 'Failed to check solution status';
      // setError(errorMessage);

      // Don't set analyzing to true for temporary errors
      // This prevents UI flickering when there are temporary network issues

      // Don't set status to failed for temporary errors
      // Continue polling despite errors to be more resilient
      log(`Continuing to poll despite error: ${errorMessage}`);
    }
  }, [finding_id, solutionStatus?.pr_url]);

  // Poll for solution status
  useEffect(() => {
    if (open && finding_id && isPolling) {
      // Initial check
      checkSolutionStatus();

      // Set up interval for subsequent calls
      intervalIdRef.current = setInterval(checkSolutionStatus, 10000);
      log(`Started polling for solution status for finding ID: ${finding_id}`);
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined;
        log(`Cleaned up solution status polling for finding ID: ${finding_id}`);
      }
    };
  }, [open, finding_id, isPolling, checkSolutionStatus]);

  // Reset all states when finding_id changes
  useEffect(() => {
    if (finding_id) {
      // Reset all states to their initial values
      setStatus('pending');
      // setError(null);
      setIsAnalyzing(true);
      setSolutionStatus(null);
      setSolution([]);
      setDisplayedSteps([]);
      setIsTerminalExpanded(true);
      setIsIssueDetailsExpanded(false);
      setExtraDetails(null);
      setJiraTaskUrl(null);
      // setJiraIssueKey(null); // Reset Jira issue key state
      setExpandedFile(null);
      setCopiedOriginal(null);
      // setCopiedFixed(null);
      setShowErrorPopup(false);
      setIsPolling(true); // Reset polling state
    }
  }, [finding_id]);

  // Check if the process is complete
  const isProcessComplete = () => {
    const lastStep = displayedSteps[displayedSteps.length - 1] || '';
    return lastStep.toLowerCase().includes('finished') ||
      lastStep.toLowerCase().includes('complete') ||
      status === 'completed' ||
      (solutionStatus && solutionStatus["Step Last"] &&
        (solutionStatus["Step Last"].toLowerCase().includes('finished') ||
          solutionStatus["Step Last"].toLowerCase().includes('complete'))) ||
      (solutionStatus?.pr_url && solutionStatus.pr_url !== '[]');
  };

  const renderProgressBar = () => {
    // Use the isProcessComplete function to determine if we should show 100%
    const progress = isProcessComplete() ? 100 : (displayedSteps.length / TOTAL_STEPS) * 100;

    return (
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
              fontWeight: 500
            }}
          >
            Progress: {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 8,
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #FF4800 0%, #FF6B00 100%)',
              borderRadius: 8,
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }
          }}
        />
      </Box>
    );
  };

  const renderTerminal = () => {
    return (
      <Box sx={{ p: 3, pt: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            gap: 1,
            cursor: 'pointer',
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
            '&:hover': {
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
              transform: 'translateX(4px)',
            }
          }}
          onClick={() => setIsTerminalExpanded(!isTerminalExpanded)}
        >
          <TerminalIcon
            sx={{
              fontSize: 20,
              color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280'
            }}
          />
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937',
              fontWeight: 500,
              flex: 1
            }}
          >
            Analysis Progress
          </Typography>
          {isTerminalExpanded ?
            <ExpandLessIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} /> :
            <ExpandMoreIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} />
          }
        </Box>

        <Collapse in={isTerminalExpanded}>
          <Box sx={{
            bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
            mx: 2.5,
            mt: 1.5,
            mb: 2,
            borderRadius: '4px',
            p: 3,
            backdropFilter: 'blur(8px)',
            background: theme => theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(26, 26, 26, 0.95) 0%, rgba(17, 17, 17, 0.98) 100%)'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: '14px',
            color: '#A9B7C6',
            position: 'relative',
            minHeight: '300px',
            maxHeight: '500px',
            overflow: 'auto',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: theme => theme.palette.mode === 'dark' ? '#333333' : '#F3F4F6',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: theme => theme.palette.mode === 'dark' ? '#4A4A4A' : '#D1D5DB',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: '#555'
              }
            }
          }}>
            {/* Steps Output */}
            {displayedSteps
              .filter(step => !step.toLowerCase().includes('pending'))
              .map((step, index) => {
                const isError = step.toLowerCase().includes('error');
                const isPR = step.includes('URL:');

                if (isPR) {
                  const [text, url] = step.split(': ');
                  return (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography sx={{ color: isError ? '#E06C75' : '#98C379', mb: 1 }}>
                        {`> ${text}`}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          sx={{
                            color: '#61AFEF',
                            fontFamily: 'monospace'
                          }}
                        >
                          {`> ${url}`}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Typography
                    key={index}
                    sx={{
                      mb: 2,
                      color: isError ? '#E06C75' : '#98C379',
                      display: 'block',
                      animation: 'typing 0.5s steps(40, end)',
                      '@keyframes typing': {
                        from: { opacity: 0 },
                        to: { opacity: 1 }
                      }
                    }}
                  >
                    {`> ${step}`}
                  </Typography>
                );
              })}

            {/* Blinking Cursor */}
            {status === 'pending' && (
              <Box
                sx={{
                  display: 'inline-block',
                  width: '8px',
                  height: '16px',
                  bgcolor: '#A9B7C6',
                  animation: 'blink 1s step-end infinite',
                  '@keyframes blink': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0 }
                  }
                }}
              />
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only allow closing through the close button, not by clicking outside or pressing ESC
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        // Reset all states
        setStatus('pending');
        // setError(null);
        setIsAnalyzing(true);
        setSolutionStatus(null);
        setSolution([]);
        setDisplayedSteps([]);
        setIsTerminalExpanded(true);
        setIsIssueDetailsExpanded(false);
        setExtraDetails(null);
        setJiraTaskUrl(null);
        // setJiraIssueKey(null); // Reset Jira issue key state when closing
        setExpandedFile(null);
        setCopiedOriginal(null);
        // setCopiedFixed(null);
        setShowErrorPopup(false);
        setIsPolling(true); // Reset polling state when closing
        onClose();
      }}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '90%',
          overflowX: 'hidden'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          bgcolor: theme => theme.palette.mode === 'dark' ? '#111111' : '#FFFFFF',
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2.5,
          bgcolor: theme => theme.palette.mode === 'dark' ? '#111111' : '#F9FAFB',
          borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'}`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: '1px',
            background: theme => `linear-gradient(90deg, ${theme.palette.mode === 'dark' ? 'rgba(255, 72, 0, 0.3)' : 'rgba(255, 72, 0, 0.2)'} 0%, ${theme.palette.mode === 'dark' ? 'rgba(255, 72, 0, 0.1)' : 'rgba(255, 72, 0, 0.1)'} 100%)`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/images/ana_logo.png"
            sx={{
              width: 28,
              height: 28,
              objectFit: 'contain'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: theme => theme.palette.mode === 'dark' ? '#F8FAFC' : '#111827',
              fontWeight: 600,
              fontSize: '1.25rem',
              letterSpacing: '-0.025em'
            }}
          >
            Ana's Solution
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: theme => theme.palette.mode === 'dark' ? '#334155' : '#F3F4F6',
                transform: 'scale(1.15)',
                color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Error Popup */}
      {showErrorPopup && (
        <Box
          onClick={() => setShowErrorPopup(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            },
            cursor: 'pointer'
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
              borderRadius: 2,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid',
              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 72, 0, 0.3)' : 'rgba(255, 72, 0, 0.2)',
              width: '320px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 4,
              position: 'relative',
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to: { opacity: 1, transform: 'translateY(0)' }
              },
              cursor: 'default'
            }}
          >
            <IconButton
              onClick={() => setShowErrorPopup(false)}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src="/images/ana_logo.png"
              sx={{
                width: 48,
                height: 48,
                objectFit: 'contain',
                mb: 3
              }}
            />
            <Typography
              sx={{
                color: theme => theme.palette.mode === 'dark' ? '#F3F4F6' : '#1F2937',
                fontSize: '1.125rem',
                fontWeight: 600,
                mb: 1,
                textAlign: 'center'
              }}
            >
              Something went wrong
            </Typography>
            <Typography
              sx={{
                color: theme => theme.palette.mode === 'dark' ? '#9CA3AF' : '#6B7280',
                fontSize: '0.875rem',
                textAlign: 'center',
                mb: 4,
                maxWidth: '90%'
              }}
            >
              There was an issue with the URL. Please try again.
            </Typography>
            <Button
              variant="contained"
              onClick={async () => {
                setShowErrorPopup(false);
                if (finding_id) {
                  try {
                    setIsAnalyzing(true);
                    setIsPolling(true); // Ensure polling is active
                    const user = getCurrentUser()

                    // First call solveWithAnaV2 to initiate a new solution
                    await solveWithAnaV2(0, 10, {
                      finding_id: finding_id,
                      branch_name: solutionStatus?.branch_name || 'main',
                      first_name: user?.first_name,
                      last_name: user?.last_name,
                    });

                    // Clear any existing interval
                    if (intervalIdRef.current) {
                      clearInterval(intervalIdRef.current);
                    }

                    // Wait for 5 seconds
                    await new Promise(resolve => setTimeout(resolve, 5000));

                    // // Initial check
                    // await checkSolutionStatus();

                    // Set up new interval for subsequent checks
                    intervalIdRef.current = setInterval(checkSolutionStatus, 10000);
                    log(`Restarted polling for solution status for finding ID: ${finding_id}`);
                  } catch (err: any) {
                    error_log('Error retrying solution:', err);
                    // setError('Failed to retry solution');
                    setStatus('failed');
                    setIsAnalyzing(false);
                    setIsPolling(false);
                  }
                }
              }}
              sx={{
                background: 'linear-gradient(90deg, #FF4800 0%, #FF6B00 100%)',
                color: '#FFFFFF',
                px: 6,
                py: 1.5,
                borderRadius: 2,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                minWidth: '120px',
                '&:hover': {
                  background: 'linear-gradient(90deg, #FF6B00 0%, #FF4800 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(255, 72, 0, 0.2)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                }
              }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      )}

      <DialogContent
        sx={{
          p: '0 24px 24px 24px',
          position: "relative",
          minHeight: "400px",
          bgcolor: theme => theme.palette.mode === 'dark' ? '#111111' : '#F9FAFB',
          overflow: "auto",
          scrollBehavior: "smooth",
          '&::-webkit-scrollbar': {
            display: "none"
          }
        }}
      >
        {/* Issue Details Card */}
        {extraDetails && (
          <Box sx={{
            mt: 2,
            mb: 3,
            bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
            borderRadius: 1,
            boxShadow: theme => theme.palette.mode === 'dark' ? '0 4px 6px rgba(0, 0, 0, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
            border: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#E5E7EB',
            overflow: 'hidden'
          }}>
            {/* Card Header */}
            <Box
              sx={{
                p: 2.5,
                borderBottom: isIssueDetailsExpanded ? '1px solid' : 'none',
                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
                }
              }}
              onClick={() => setIsIssueDetailsExpanded(!isIssueDetailsExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BugReportIcon
                  sx={{
                    fontSize: 24,
                    color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280'
                  }}
                />
                <Typography sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937',
                }}>
                  Issue Details
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {jiraTaskUrl ? (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent collapse toggle
                      window.open(jiraTaskUrl, '_blank');
                    }}
                    sx={{
                      background: 'linear-gradient(90deg, #2563EB 0%, #3B82F6 100%)',
                      color: '#FFFFFF',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #3B82F6 0%, #2563EB 100%)',
                      }
                    }}
                  >
                    Go to Jira Task
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent collapse toggle
                      if (extraDetails?.scan_id) {
                        // Map scan type to the correct URL path based on App.tsx routes
                        let url = '';
                        const scanTypeValue: string = extraDetails.scan_type?.toUpperCase() || scanType?.toUpperCase() || '';

                        switch (scanTypeValue) {
                          case 'SAST':
                            url = `/sast/scan/${extraDetails.scan_id}`;
                            break;
                          case 'SBOM':
                            url = `/sbom/scan/${extraDetails.scan_id}`;
                            break;
                          case 'SCA':
                            url = `/sca/scan/${extraDetails.scan_id}`;
                            break;
                          case 'SECRET':
                            url = `/secrets/scan/${extraDetails.scan_id}`;
                            break;
                          case 'API':
                            url = `/api-security/scan/${extraDetails.scan_id}`;
                            break;
                          case 'THREAT-MODELING':
                          case 'THREAT_MODELING':
                            url = `/threat-modeling/scan/${extraDetails.scan_id}`;
                            break;
                          case 'DAST':
                            url = `/dast/scan/${extraDetails.scan_id}`;
                            break;
                          case 'INFRA':
                            url = `/infra/scan/${extraDetails.scan_id}`;
                            break;
                          default:
                            // Fallback to a generic format
                            url = `/${(extraDetails.scan_type || scanType || 'sast').toLowerCase()}/scan/${extraDetails.scan_id}`;
                        }

                        // Open in same tab but keep popup open
                        window.open(url, '_self');
                      }
                    }}
                    sx={{
                      background: 'linear-gradient(90deg, #FF4800 0%, #FF6B00 100%)',
                      color: '#FFFFFF',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #FF6B00 0%, #FF4800 100%)',
                      }
                    }}
                  >
                    Go to Scan
                  </Button>
                )}
                {isIssueDetailsExpanded ?
                  <ExpandLessIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} /> :
                  <ExpandMoreIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} />
                }
              </Box>
            </Box>

            {/* Issue Details Content */}
            <Collapse in={isIssueDetailsExpanded}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                      mb: 0.5
                    }}>
                      Project Name
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                    }}>
                      {extraDetails.project_name || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                      mb: 0.5
                    }}>
                      Issue ID
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                      }}>
                        {extraDetails.slug || 'N/A'}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (extraDetails.finding_id) {
                            navigator.clipboard.writeText(extraDetails.slug.toString());
                          }
                        }}
                        sx={{
                          p: 0.5,
                          color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                          '&:hover': {
                            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                            color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                          }
                        }}
                      >
                        <CopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                      mb: 0.5
                    }}>
                      Category
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                    }}>
                      {extraDetails.scan_type || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                      mb: 0.5
                    }}>
                      File Name
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                    }}>
                      {extraDetails.filepath ? extraDetails.filepath.split('/').pop() : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      color: theme => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                      mb: 0.5
                    }}>
                      Title
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937'
                    }}>
                      {extraDetails.finding_title || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}
        {/* Progress and Terminal Card */}
        <Box sx={{
          mt: 2,
          bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
          borderRadius: 1,
          boxShadow: theme => theme.palette.mode === 'dark' ? '0 4px 6px rgba(0, 0, 0, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
          border: '1px solid',
          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#E5E7EB',
          overflow: 'hidden'
        }}>
          {/* Card Header */}
          <Box sx={{
            p: 2.5,
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon
                  sx={{
                    fontSize: 24,
                    color: (theme: any) => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280'
                  }}
                />
                <Typography sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937',
                }}>
                  Scan Progress
                </Typography>
              </Box>
              {solutionStatus?.pr_url && solutionStatus.pr_url !== '[]' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => {
                    const url = solutionStatus.pr_url;
                    if (url && typeof url === 'string' && url.match(/https?:\/\/(?:www\.)?(?:github\.com|bitbucket\.org)\/[^\s]+\/[^\s]+/)) {
                      // Open in new tab
                      window.open(url, '_blank');
                    }
                  }}
                  disabled={!solutionStatus?.pr_url || solutionStatus.pr_url === '' || !solutionStatus.pr_url.replace(/[[\]']/g, '').match(/https?:\/\/(?:www\.)?(?:github\.com|bitbucket\.org)\/[^\s]+\/[^\s]+/)}
                  sx={{
                    background: 'linear-gradient(90deg, #FF4800 0%, #FF6B00 100%)',
                    color: '#FFFFFF',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #FF6B00 0%, #FF4800 100%)',
                    },
                    '&.Mui-disabled': {
                      background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                      color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
                    }
                  }}
                >
                  Open PR
                </Button>
              )}
            </Box>
          </Box>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                minHeight: '300px'
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: '#FF4800',
                  mb: 3
                }}
              />
              <Typography
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#F3F4F6' : '#1F2937',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  fontWeight: 500
                }}
              >
                {`Initializing ${scanType || 'Security'} Solution...`}
              </Typography>
            </Box>
          ) : isAnalyzing ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                minHeight: '300px'
              }}
            >
              <CircularProgress
                size={48}
                sx={{
                  color: '#FF4800',
                  mb: 3
                }}
              />
              <Typography
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#F3F4F6' : '#1F2937',
                  textAlign: 'center',
                  fontSize: '1.125rem',
                  fontWeight: 500
                }}
              >
                Analyzing vulnerability details...
              </Typography>
            </Box>
          ) : (
            <>
              {renderProgressBar()}
              {renderTerminal()}
            </>
          )}
        </Box>

        {/* Code Changes Card */}
        {solution?.length > 0 && !loading && !isAnalyzing && (
          <Box sx={{
            mt: 3,
            bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
            borderRadius: 1,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            border: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
            overflow: 'hidden'
          }}>
            {/* Card Header */}
            <Box sx={{
              p: 2.5,
              borderBottom: '1px solid',
              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <CodeIcon
                sx={{
                  fontSize: 24,
                  color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280'
                }}
              />
              <Typography sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937',
              }}>
                Security Fixes & Code Changes
              </Typography>
            </Box>
            {solution.map((file: SolutionFile) => {
              return (
                <Box key={file.id} sx={{
                  mb: 0.2,
                  '&:not(:first-of-type)': {
                    mt: 0.2
                  }
                }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 2,
                      gap: 1.5,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderRadius: '4px',
                      borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.03)',
                        transform: 'translateX(4px)',
                      }
                    }}
                    onClick={() => setExpandedFile(expandedFile === file.filename ? null : file.filename)}
                  >
                    <FileIcon
                      sx={{
                        fontSize: 20,
                        color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280'
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: theme => theme.palette.mode === 'dark' ? '#E2E8F0' : '#1F2937',
                        fontWeight: 500,
                        flex: 1,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      }}
                    >
                      {file.filename}
                    </Typography>
                    {expandedFile === file.filename ?
                      <ExpandLessIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} /> :
                      <ExpandMoreIcon sx={{ fontSize: 20, color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280' }} />
                    }
                  </Box>

                  <Collapse in={expandedFile === file.filename}>
                    <Box sx={{
                      p: 2,
                      bgcolor: theme => theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
                      height: 'auto',
                      maxHeight: '600px',
                      mb: 1,
                      overflowX: 'auto',
                      '& pre': {
                        margin: 0,
                        padding: '3px !important'
                      },
                      '& .diff-code-container': {
                        margin: '0 !important'
                      },
                      '& .diff-gutter-col': {
                        padding: '0 3px !important'
                      },
                      '& td': {
                        padding: '0 !important'
                      },
                      '& .title-header': {
                        padding: '2px !important',
                        fontSize: '12px !important'
                      }
                    }}>
                      {/* <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                        px: 1,
                        '& .diff-header': {
                          display: 'flex',
                          alignItems: 'center',
                          width: '50%',
                          position: 'relative'
                        }
                      }}> */}
                      {/* <Box className="diff-header" sx={{ pr: 2 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            justifyContent: 'space-between'
                          }}>
                            <Typography sx={{
                              fontSize: '12px',
                              color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
                              fontWeight: 500,
                              flex: 1
                            }}>
                              Original Code (Vulnerable)
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(file.file_content || '');
                                setCopiedOriginal(file.id);
                                setTimeout(() => setCopiedOriginal(null), 2000);
                              }}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                '&:hover': {
                                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              {copiedOriginal === file.id ? (
                                <CheckIcon sx={{ fontSize: '14px' }} />
                              ) : (
                                <CopyIcon sx={{ fontSize: '14px' }} />
                              )}
                            </IconButton>
                          </Box>
                        </Box>
                        <Box className="diff-header" sx={{ pl: 2 }}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            justifyContent: 'space-between'
                          }}>
                            <Typography sx={{
                              fontSize: '12px',
                              color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
                              fontWeight: 500,
                              flex: 1
                            }}>
                              Fixed Code (Secure)
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(file.fixed_content || '');
                                setCopiedFixed(file.id);
                                setTimeout(() => setCopiedFixed(null), 2000);
                              }}
                              sx={{
                                p: 0.5,
                                ml: 0.5,
                                '&:hover': {
                                  bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              {copiedFixed === file.id ? (
                                <CheckIcon sx={{ fontSize: '14px' }} />
                              ) : (
                                <CopyIcon sx={{ fontSize: '14px' }} />
                              )}
                            </IconButton>
                          </Box>
                        </Box>
                      </Box> */}
                      {/* Using the same approach as CustomDiffViewer to check for identical files */}
                      {(() => {
                        // Function to normalize line endings and parse escaped newlines
                        const normalizeContent = (content: string): string => {
                          // First replace literal '\n' strings with actual newlines
                          const parsedContent = typeof content === 'string' 
                            ? content.replace(/\\n/g, '\n') 
                            : content || '';
                          // Then normalize line endings
                          return parsedContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                        };
                        
                        // Function to find differences between two strings line by line
                        const findDifferences = () => {
                          const oldLines = normalizeContent(file.file_content || '').split('\n');
                          const newLines = normalizeContent(file.fixed_content || '').split('\n');
                          
                          const differences: {
                            lineNumber: number, 
                            oldLine: string, 
                            newLine: string, 
                            type: 'added' | 'removed' | 'modified'
                          }[] = [];
                          
                          // Find the maximum length to iterate through
                          const maxLength = Math.max(oldLines.length, newLines.length);
                          
                          for (let i = 0; i < maxLength; i++) {
                            const oldLine = i < oldLines.length ? oldLines[i] : '';
                            const newLine = i < newLines.length ? newLines[i] : '';
                            
                            // Compare lines after trimming whitespace
                            if (oldLine.trim() !== newLine.trim()) {
                              // Determine if line was added, removed, or modified
                              let type: 'added' | 'removed' | 'modified' = 'modified';
                              if (oldLine.trim() === '') {
                                type = 'added';
                              } else if (newLine.trim() === '') {
                                type = 'removed';
                              }
                              
                              differences.push({
                                lineNumber: i,
                                oldLine,
                                newLine,
                                type
                              });
                            }
                          }
                          
                          return differences;
                        };
                        
                        // Find differences
                        const differences = findDifferences();
                        
                        // If there are no differences, show a message
                        if (differences.length === 0) {
                          return (
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 'auto',
                              minHeight: '40px',
                              maxHeight: '60px',
                              flexDirection: 'row',
                              gap: 2,
                              py: 1
                            }}>
                              <Typography sx={{
                                fontSize: '0.875rem',
                                color: theme => theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
                                fontWeight: 500
                              }}>
                                The file content is identical. No changes needed.
                              </Typography>
                              <Tooltip title={copiedOriginal === file.id ? "Copied!" : "Copy code"}>
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(file.file_content || '');
                                    setCopiedOriginal(file.id);
                                    setTimeout(() => setCopiedOriginal(null), 2000);
                                  }}
                                  sx={{
                                    color: theme.palette.mode === 'dark' ? '#CBD5E1' : '#6B7280',
                                    padding: '4px',
                                    '&:hover': {
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                      color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827'
                                    }
                                  }}
                                >
                                  {copiedOriginal === file.id ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        } else {
                          // If there are differences, show the diff viewer
                          return (
                            <FileDiffViewer oldText={file.file_content || ''} newText={file.fixed_content || ''} />
                            // <MyDiffViewer 
                            //   oldValue={normalizeContent(file.file_content || '')} 
                            //   newValue={normalizeContent(file.fixed_content || '')} 
                            //   splitView={true} 
                            // />
                          );
                        }
                      })()}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
